<template>
  <div v-if="isCalculable" class="simulator-results">
    <div class="result_block no-print">
      <h3>Résultat</h3>

      <!-- Summary rows + salary cards -->
      <div id="global-result" class="segment">
        <div class="d-flex flex-wrap justify-content-between py-3 border-bottom">
          <span>{{ paidToEmployeeLabel }}</span>
          <strong class="result-value">{{ toMoney(netPaidTotal) }} / mois</strong>
        </div>
        <div class="d-flex flex-wrap justify-content-between py-3 border-bottom">
          <span>Coût pour l'employeur AVANT aides</span>
          <strong class="result-value">{{ toMoney(totalAmount) }} / mois</strong>
        </div>
        <div class="py-3">
          Reste à charge pour l'employeur selon ses revenus mensuels {{ incomeReferenceYear }} (net imposable)
        </div>

        <!-- Salary cards -->
        <div class="d-flex flex-wrap gap-3">
          <div
            v-for="salary in averageMonthlySalaries"
            :key="salary"
            class="cmg-salary-card flex-fill"
            :class="{ 'cmg-salary-card--danger': cmgNotAvailable }"
          >
            <div class="cmg-salary-card__title">Salaire de {{ salary }}&nbsp;€</div>
            <div class="cmg-salary-card__value" :class="['blur', { blurring: isPublic, 'text-danger': cmgNotAvailable }]">
              {{ toMoney(amountLeftPayable(salary)) }} / mois
            </div>
          </div>
        </div>

        <!-- Warnings -->
        <div v-if="childTooOld && cmgAidToggle !== 'no'" class="alert alert-warning alert--small mt-3">
          <p>
            L'employeur n'est pas éligible à l'aide "Complément de libre choix du Mode de Garde"
            <SourceLink v-if="cafCmgSource" :url="cafCmgSource" :add-source-text="true" />
            car l'enfant a déjà passé son
            <span v-if="childAge === '3'" class="font--bold">sixième anniversaire.</span>
            <span v-if="childAge === '4'" class="font--bold">douzième anniversaire.</span>
          </p>
          <p>
            <strong>L'employeur ne peut pas obtenir la prise en charge des cotisations sociales
              (salariales et patronales). Il devra donc les payer à l'URSSAF.</strong>
            <SourceLink v-if="pajemploiCmgSource" :url="pajemploiCmgSource" :add-source-text="true" />
          </p>
        </div>

        <div v-if="cmgAidToggle === 'no'" class="alert alert-danger alert--small mt-3">
          <p>
            L'employeur n'est pas éligible à l'aide "Complément de libre choix du Mode de Garde"
            <SourceLink v-if="cafCmgSource" :url="cafCmgSource" :add-source-text="true" />
            car vous devez avoir une activité professionnelle, si vous avez recours à une association ou une entreprise,
            l'enfant doit être accueilli au moins 16 heures dans le mois.
          </p>
          <p>
            <strong>L'employeur ne peut pas obtenir la prise en charge des cotisations sociales
              (salariales et patronales). Il devra donc les payer à l'URSSAF.</strong>
            <SourceLink v-if="pajemploiCmgSource" :url="pajemploiCmgSource" :add-source-text="true" />
          </p>
        </div>
      </div>

      <!-- Detail accordion: Cost breakdown -->
      <div class="segment">
        <AccordionGroup class="accordion--breakable" :exclusive="true">
          <AccordionItem title="Détail du calcul de coût total">
            <div id="monthly-resume" class="monthly_detail compact-print" :class="{ 'no-print': isPublic }">
              <div class="d-flex flex-wrap justify-content-between py-3 border-bottom">
                <span>Mensualisation <Popup v-if="origin === 'single_tool'" :icon="iconQuestion" :body="mensHelp" /></span>
                <span :class="blurClass">{{ toMoney(grossMensualization) }} brut <i>(environ {{ toMoney(netMensualization) }} net)</i></span>
              </div>
              <div v-if="hasOvertime" class="d-flex flex-wrap justify-content-between py-3 border-bottom">
                <span>Estimation de la majoration heures majorées <Popup :icon="iconQuestion" :body="overtimeHelp" /></span>
                <span :class="blurClass">{{ toMoney(grossMonthlyOvertimeWagePremium) }} brut <i>(environ {{ toMoney(netMonthlyOvertimeIncrease) }} net)</i></span>
              </div>
              <div class="d-flex flex-wrap justify-content-between py-3 border-bottom">
                <span>Congés payés{{ isFullYear ? '' : ' *' }} <Popup :icon="iconQuestion" :body="vacationHelp" /></span>
                <span :class="blurClass">{{ isFullYear ? 'Inclus dans la mensualisation' : 'Exclus du calcul *' }}</span>
              </div>
              <div v-if="!isGed" class="d-flex flex-wrap justify-content-between py-3 border-bottom">
                <span>Estimation indemnités d'entretien <Popup :icon="iconQuestion" :body="maintenanceHelp" /></span>
                <span :class="blurClass">{{ maintenanceAllowanceMonthly > 0 ? 'Environ ' : '' }}{{ toMoney(maintenanceAllowanceMonthly) }}</span>
              </div>
              <div v-if="!isGed" class="d-flex flex-wrap justify-content-between py-3 border-bottom">
                <span>Estimation indemnités repas <Popup :icon="iconQuestion" :body="mealHelp" /></span>
                <span :class="blurClass">{{ mealAllowanceMonthly > 0 ? 'Environ ' : '' }}{{ toMoney(mealAllowanceMonthly) }}</span>
              </div>
              <div class="d-flex flex-wrap justify-content-between py-3 border-bottom">
                <span>{{ paidToEmployeeDetailLabel }} <Popup :icon="iconQuestion" :body="paidToEmployeeHelp" /></span>
                <span>Environ {{ toMoney(netPaidTotal) }} / mois</span>
              </div>
              <div class="d-flex flex-wrap justify-content-between py-3 border-bottom">
                <span>Estimation cotisations salariales <Popup :icon="iconQuestion" :body="employeeContributionsHelp" /></span>
                <span>Environ {{ toMoney(socialContributionsEmployee) }} <i>(incluses dans le brut)</i></span>
              </div>
              <div class="d-flex flex-wrap justify-content-between py-3 border-bottom">
                <span>Estimation cotisations patronales <Popup :icon="iconQuestion" :body="employerContributionsHelp" /></span>
                <span>Environ {{ toMoney(socialContributionsEmployer) }}</span>
              </div>
              <div class="d-flex flex-wrap justify-content-between py-3">
                <strong>TOTAL AVANT AIDES</strong>
                <strong>{{ toMoney(totalAmount) }}</strong>
              </div>
              <div v-if="!isFullYear" class="font--xs mt-3">
                <p class="font--xs">* Pour ce type de contrat, le paiement des congés payés dépend du mode de paiement choisi au contrat.
                  Il peut intervenir intégralement au mois de juin, au fur et à mesure de la prise des congés, ou encore lors de la prise principale des congés.
                  Le montant total des congés sur l'année est estimé à {{ toMoney(netVacationsMonthly * 12) }} net ({{ toMoney(grossVacationsMonthly * 12) }} brut)
                  sur l'année, soit {{ toMoney(netVacationsMonthly) }} net ({{ toMoney(grossVacationsMonthly) }} brut) si ramené au mois.
                  Cependant, le paiement des congés <b>ne peut pas être mensualisé pour ce type de contrat,</b> cette somme n'est donc pas incluse dans la simulation.</p>
              </div>
            </div>
          </AccordionItem>
          <AccordionItem title="Détail du calcul des aides">
            <div id="employer-cost" class="final_cost" :class="{ 'no-print': isPublic }">
              <div class="table-responsive">
                <table id="paje-details" class="table table-striped text-end align-middle font--xs simulator-cmg__cost-details">
                  <thead>
                    <tr>
                      <th class="text-start">
                        Salaire mensuel (net imposable) de l'employeur en {{ incomeReferenceYear }}
                      </th>
                      <th v-for="salary in averageMonthlySalaries" :key="salary">
                        {{ toMoney(salary) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="text-start">
                        Total mensuel
                        <Popup :icon="iconQuestion" :body="totalMonthlyHelp" />
                      </td>
                      <td v-for="_ in averageMonthlySalaries.length" :key="_">
                        {{ toMoney(totalAmount) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-start">
                        {{ cafCoverageLabel }}
                        <Popup :icon="iconQuestion" :body="cafCoverageHelp" />
                      </td>
                      <td v-for="_ in averageMonthlySalaries.length" :key="_" :class="blurClass">
                        - {{ toMoney(socialContributionsCredit) }}
                      </td>
                    </tr>
                    <tr v-if="isGed">
                      <td class="text-start">
                        Abattement de charges patronales
                        <Popup :icon="iconQuestion" :body="employerReductionHelp" />
                      </td>
                      <td v-for="_ in averageMonthlySalaries.length" :key="_" :class="blurClass">
                        - {{ toMoney(employerContributionsReduction) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-start">
                        Aide CMG attribuée
                        <Popup :icon="iconQuestion" :body="cmgAidHelp" />
                      </td>
                      <td v-for="salary in averageMonthlySalaries" :key="salary" :class="blurClass">
                        - {{ toMoney(cmgEligibleAmount(salary)) }}
                      </td>
                    </tr>
                    <tr v-if="otherAidToggle">
                      <td class="text-start">
                        Autres aides éventuelles
                        <Popup :icon="iconQuestion" :body="`<p>Sources possibles d'aides supplémentaires :</p><ul><li>Comité d'entreprise</li><li>Département</li><li>Ville</li><li>Autre</li></ul>`" />
                      </td>
                      <td v-for="_ in averageMonthlySalaries.length" :key="_" :class="blurClass">
                        - {{ toMoney(otherAidAmount) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-start">
                        <strong>Reste à charge mensuel (estimation)</strong>
                        <Popup :icon="iconQuestion" :body="`<p>Le montant final estimé que l'employeur devra débourser chaque mois (estimation à titre indicatif uniquement)</p>`" />
                      </td>
                      <td v-for="salary in averageMonthlySalaries" :key="salary" :class="[blurClass, 'font--bold']">
                        {{ toMoney(amountLeftPayableBeforeTaxes(salary)) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-start">
                        Crédit d'impôt
                        <Popup :icon="iconQuestion" :body="`<p>L'employeur bénéficie également d'un crédit d'impôt s'élevant à <b>50%</b> des dépenses supportées pour la garde d'un enfant <b>de moins de 6 ans</b> par un(e) assistant(e) maternel(le).</p><p>Ces dépenses comprennent le salaire de l'assistant(e) maternel(le), ainsi que les indemnités d'entretien <b>(dans une limite de 2,65€ par jour d'accueil)</b>.</p><p>Les aides perçues pour la garde de l'enfant doivent être déduites de ce montant (y compris l'aide CMG).</p><p>Le crédit d'impôt est calculé sur une base limite de <strong>3500 €</strong> par enfant, soit un maximum de <strong>1750 €</strong> de crédit d'impôt par enfant.${externalSources.credit_impot ? ' <a href=&quot;' + externalSources.credit_impot + '&quot; target=&quot;_blank&quot; rel=&quot;noopener noreferrer&quot; class=&quot;source-link&quot;>(source)</a>' : ''}</p>`" />
                      </td>
                      <td v-for="salary in averageMonthlySalaries" :key="salary" :class="blurClass">
                        - {{ toMoney(taxesCredit(salary) / 12) }}
                        <br />
                        <i class="font--xs-light">({{ toMoney(taxesCredit(salary)) }} annuel)</i>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-start">
                        <strong>Coût mensuel réel</strong>
                        <Popup :icon="iconQuestion" :body="`<p>C'est le coût final estimé à charge de l'employeur pour chaque mois d'accueil, après déduction des aides CMG et du crédit d'impôt</p>`" />
                      </td>
                      <td v-for="salary in averageMonthlySalaries" :key="salary" :class="[blurClass, 'font--bold']">
                        {{ toMoney(amountLeftPayable(salary)) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-start">
                        Coût journalier réel
                        <Popup :icon="iconQuestion" :body="`<p>C'est le coût final estimé à charge de l'employeur pour chaque jour d'accueil, après déduction des aides CMG et du crédit d'impôt</p>`" />
                      </td>
                      <td v-for="salary in averageMonthlySalaries" :key="salary" :class="blurClass">
                        {{ toMoney(dailyCost(salary)) }} / jour
                      </td>
                    </tr>
                    <tr>
                      <td class="text-start">
                        Coût horaire réel
                        <Popup :icon="iconQuestion" :body="`<p>C'est le coût final estimé à charge de l'employeur pour chaque heure d'accueil, après déduction des aides CMG et du crédit d'impôt</p>`" />
                      </td>
                      <td v-for="salary in averageMonthlySalaries" :key="salary" :class="blurClass">
                        {{ toMoney(hourlyCost(salary)) }} / heure
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </AccordionItem>
        </AccordionGroup>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import {
  AccordionGroup,
  AccordionItem,
  Popup,
  SourceLink,
  iconQuestion,
} from '@twg/ui';

export default defineComponent({
  name: 'SimulatorResults',
  components: { AccordionGroup, AccordionItem, Popup, SourceLink },
  props: {
    careType: { type: String, default: 'assmat' },
    employerContributionsReduction: { type: Number, default: 0 },
    isCalculable: { type: Boolean, required: true },
    isPublic: { type: Boolean, default: false },
    origin: { type: String, default: 'single_tool' },
    hasOvertime: { type: Boolean, default: false },
    // Formatting functions
    toMoney: { type: Function, required: true },
    // Computed values
    grossMensualization: { type: Number, default: 0 },
    netMensualization: { type: Number, default: 0 },
    grossMonthlyOvertimeWagePremium: { type: Number, default: 0 },
    netMonthlyOvertimeIncrease: { type: Number, default: 0 },
    netPaidTotal: { type: Number, default: 0 },
    socialContributionsEmployee: { type: Number, default: 0 },
    socialContributionsEmployer: { type: Number, default: 0 },
    totalAmount: { type: Number, default: 0 },
    grossTotal: { type: Number, default: 0 },
    maintenanceAllowanceMonthly: { type: Number, default: 0 },
    mealAllowanceMonthly: { type: Number, default: 0 },
    socialContributionsCredit: { type: Number, default: 0 },
    averageMonthlySalaries: { type: Array, default: () => [] },
    incomeReferenceYear: { type: Number, default: () => new Date().getFullYear() - 2 },
    // Methods
    cmgEligibleAmount: { type: Function, required: true },
    amountLeftPayable: { type: Function, required: true },
    amountLeftPayableBeforeTaxes: { type: Function, required: true },
    taxesCredit: { type: Function, required: true },
    dailyCost: { type: Function, required: true },
    hourlyCost: { type: Function, required: true },
    // CMG state
    cmgNotAvailable: { type: Boolean, default: false },
    childTooOld: { type: Boolean, default: false },
    cmgAidToggle: { type: String, default: 'yes' },
    otherAidToggle: { type: Boolean, default: false },
    otherAidAmount: { type: Number, default: 0 },
    handicapToggle: { type: Boolean, default: false },
    handicapToggleInfos: { type: String, default: '' },
    situationFrench: { type: String, default: '' },
    childrenCount: { type: String, default: '' },
    childAgeFrench: { type: String, default: '' },
    childAge: { type: String, default: '1' },
    externalSources: { type: Object, default: () => ({}) },
    isAuthenticated: { type: Boolean, default: false },
    brandName: { type: String, default: '' },
    isFullYear: { type: Boolean, default: true },
    netVacationsMonthly: { type: Number, default: 0 },
    grossVacationsMonthly: { type: Number, default: 0 },
  },
  setup(props) {
    const isGed = computed(() => props.careType === 'ged');
    const cafCmgSource = computed(() => props.externalSources.caf_ged_CMG || props.externalSources.caf_CMG);
    const pajemploiCmgSource = computed(() => props.externalSources.pajemploi_ged_CMG || props.externalSources.pajemploi_CMG);
    const blurClass = computed(() => ['blur', { blurring: props.isPublic }]);
    const toolsLink = computed(() => props.isAuthenticated ? '/home' : '/');

    const paidToEmployeeLabel = computed(() => isGed.value
      ? 'Montant payé au salarié'
      : 'Montant payé à l\'assistant(e) maternel(le)');
    const paidToEmployeeDetailLabel = computed(() => isGed.value
      ? 'Montant moyen payé au salarié'
      : 'Montant moyen payé à l\'assistant(e) maternel(le)');
    const paidToEmployeeHelp = computed(() => isGed.value
      ? '<p>C\'est la somme :</p><ul><li>du salaire net,</li><li>des heures majorées éventuelles.</li></ul>'
      : '<p>C\'est la somme :</p><ul><li>du salaire net (congés payés inclus si année complète),</li><li>des heures majorées éventuelles,</li><li>et des indemnités d\'entretien et de repas.</li></ul><p>En année incomplète, les congés payés sont réglés en plus, selon le mode de paiement choisi au contrat : ils ne sont pas inclus dans ce montant.</p>');

    // Assmat: vacation pay is inside the mensualisation in a full year only.
    // GED has no année complète / incomplète, so the contribution base is just
    // salary + overtime (same as useCmgCalculationsGed grossTotal).
    const contributionsBaseHelp = computed(() => isGed.value
      ? 'Elles sont appliquées sur le salaire et les heures majorées éventuelles.'
      : 'Elles sont appliquées sur le salaire (congés payés inclus si année complète) et les heures majorées éventuelles.');
    const employeeContributionsHelp = computed(() =>
      `<p>Les cotisations salariales sont calculées avec <b>les taux applicables à ce jour.</b></p><p>${contributionsBaseHelp.value}</p>`);
    const employerContributionsHelp = computed(() =>
      `<p>Les cotisations patronales sont calculées avec <b>les taux applicables à ce jour.</b></p><p>${contributionsBaseHelp.value}</p>`);

    const totalMonthlyHelp = computed(() => {
      if (isGed.value) {
        return `<p>Il s'agit de la somme :</p><table class='table'><tr><td>Salaire brut (incluant majoration)</td><td>${props.toMoney(props.grossTotal)}</td></tr><tr><td>Cotisations patronales</td><td>${props.toMoney(props.socialContributionsEmployer)}</td></tr></table>`;
      }
      return `<p>Il s'agit de la somme :</p><table class='table'><tr><td>Salaire brut (incluant majoration et congés éventuels)</td><td>${props.toMoney(props.grossTotal)}</td></tr><tr><td>Indemnités d'entretiens</td><td>${props.toMoney(props.maintenanceAllowanceMonthly)}</td></tr><tr><td>Indemnités repas</td><td>${props.toMoney(props.mealAllowanceMonthly)}</td></tr><tr><td>Cotisations patronales</td><td>${props.toMoney(props.socialContributionsEmployer)}</td></tr></table>`;
    });

    const employerReductionHelp = '<p>Abattement de charges patronales appliqué sur 90% des heures normales et 100% des heures majorées, plafonné à 50% des cotisations sociales.</p>';

    // Assmat: the CAF covers the full contributions ("directe"); GED: capped
    // per child age, hence partial. Keep the historical assmat wording.
    const cafCoverageLabel = computed(() => isGed.value
      ? 'Prise en charge partielle des cotisations sociales par la CAF/Pajemploi'
      : 'Prise en charge directe des cotisations sociales par la CAF/Pajemploi');

    const mensHelp = computed(() => {
      let body = '<p>C\'est le calcul du salaire lissé sur l\'année.</p>';
      if (props.origin === 'single_tool') {
        const link = props.isAuthenticated ? '/home' : '/signup_flow';
        body += `<div class="help-notice small notice"><div class="bubble"><p>Vous pouvez en avoir tous les détails en utilisant notre <a href="${link}" class="dotted" target="_blank">éditeur complet de contrat</a> !</p></div></div>`;
      }
      return body;
    });

    const overtimeHelp = computed(() => {
      const threshold = isGed.value ? 40 : 45;
      let body = `<p>Les heures majorées sont les heures au-delà de ${threshold} heures par semaine.</p>` +
        '<p>Elles sont majorées du taux convenu au contrat. Le calcul fait <b>une estimation</b> de cette majoration moyenne par mois.</p>';
      body += '<div class="help-notice small notice"><div class="bubble">' +
        '<p><strong class="red">Attention, ce montant est une estimation :</strong><br/>' +
        'Pour obtenir le montant exact dû chaque mois, il faudra analyser les durées effectives d\'accueil (réel ou facturé) et décompter les heures majorées en fonction.</p>' +
        `<p>Si vous utilisez <a href="${toolsLink.value}" class="dotted" target="_blank">nos outils de gestion</a>, tout cela se fait tout seul !</p>` +
        '</div></div>';
      return body;
    });

    const vacationHelp = computed(() => {
      let body = '<p>Le montant annuel des congés payés est calculé par la comparaison entre deux méthodes de calcul :</p>' +
        '<ul class="relaxed"><li>la méthode dite des "10%"</li><li>la méthode dite du "maintien de salaire"</li></ul>' +
        '<p>Le montant le plus important entre ces deux résultats doit être retenu. Nous ramenons ici ce résultat au mois pour l\'estimation du coût final.</p>';
      if (!isGed.value) {
        body += '<p><strong>Note :</strong> En année complète, s\'il n\'y a pas de dépassement d\'horaires, c\'est le maintien de salaire qui est généralement le plus avantageux. Il est déjà inclus dans la mensualisation calculée.</p>';
      }
      body += '<div class="help-notice small notice"><div class="bubble">' +
        '<p><strong class="red">Attention ce montant n\'est qu\'une estimation, qui fait l\'hypothèse d\'un accueil strictement conforme au contrat !</strong></p>' +
        '<p>Il faudra comptabiliser <b>l\'acquisition réelle des congés</b> au fur et à mesure de l\'accueil, selon les absences et temps complémentaires éventuels.</p>' +
        '<p>Et ensuite calculer chaque mois le montant dû en fonction du mode de paiement que vous avez choisi au contrat.' +
        (props.origin === 'single_tool' ? ` Si vous utilisez <a href="${toolsLink.value}" class="dotted" target="_blank">nos outils de gestion</a>, tout cela se fait tout seul !` : '') +
        '</p></div></div>';
      return body;
    });

    const maintenanceHelp = computed(() => {
      let body = '<p>Les indemnités d\'entretien sont estimées en prenant leur montant journalier, multiplié par le nombre de jours d\'accueil moyen par mois.</p>' +
        '<p>En année incomplète, les semaines d\'absences planifiées sont prises en compte dans le calcul.</p>' +
        '<p>En année complète, les 5 semaines de congés de l\'AssMat sont également prises en compte.</p>';
      body += '<div class="help-notice small notice"><div class="bubble">' +
        '<p><strong class="red">Attention ce montant évoluera bien sûr chaque mois !</strong></p>' +
        '<p>Il faudra comptabiliser les jours réels d\'accueil et vérifier la conformité au minimum légal en fonction de la durée d\'accueil du jour.</p>' +
        `<p><b>Il ne s'agit là que d'une estimation</b>. Pour calculer précisément le montant dû chaque mois, vous pouvez utiliser <a href="${toolsLink.value}" class="dotted" target="_blank">nos outils de gestion</a> qui calculent tout automatiquement !</p>` +
        '</div></div>';
      return body;
    });

    const mealHelp = computed(() => {
      let body = '<p>Les indemnités repas sont estimées en prenant leur montant journalier, multiplié par le nombre de jours d\'accueil moyen par mois.</p>' +
        '<p>En année incomplète, les semaines d\'absences planifiées sont prises en compte dans le calcul.</p>' +
        '<p>En année complète, les 5 semaines de congés de l\'AssMat sont également prises en compte.</p>';
      body += '<div class="help-notice small notice"><div class="bubble">' +
        '<p><strong class="red">Attention ce montant évoluera bien sûr chaque mois !</strong></p>' +
        '<p>Il faudra comptabiliser les jours réels d\'accueil et vérifier la conformité au minimum légal en fonction de la durée d\'accueil du jour.</p>' +
        `<p><b>Il ne s'agit là que d'une estimation</b>. Pour calculer précisément le montant dû chaque mois, vous pouvez utiliser <a href="${toolsLink.value}" class="dotted" target="_blank">nos outils de gestion</a> qui calculent tout automatiquement !</p>` +
        '</div></div>';
      return body;
    });

    const cafCoverageHelp = computed(() => {
      let body = isGed.value
        ? '<p>Si vous bénéficiez de l\'aide CMG, la CAF prend en charge partiellement le paiement des cotisations sociales pour vous, dans la limite d\'un plafond qui dépend de l\'âge de l\'enfant.</p>'
        : '<p>Si vous bénéficiez de l\'aide CMG, la CAF prend en charge le paiement des cotisations salariales et patronales pour vous.</p>' +
          '<p>Cette prise en charge ne dépend que de l\'âge du plus jeune enfant accueilli et des caractéristiques du contrat (respect du plafond journalier).</p>' +
          '<p>Elle ne dépend pas des revenus de l\'employeur.</p>';
      const name = props.brandName || 'notre outil';
      body += '<div class="help-notice small notice"><div class="bubble">' +
        `<p>Si vous utilisez <a href="${toolsLink.value}" class="dotted" target="_blank">nos outils de gestion</a>, ${name} vérifie chaque mois votre droit à la prise en charge des cotisations, <b>et vous avertit en cas de dépassement !</b></p>` +
        '</div></div>';
      return body;
    });

    const cmgAidHelp = computed(() => {
      let body = '<p>Le montant calculé dépend de la situation que vous avez notifié plus haut sur le simulateur selon les nouvelles directives de la CAF, en tenant en compte :</p>' +
        '<ul><li>du salaire réel du salarié</li><li>des ressources mensuelles du foyer</li>' +
        `<li>de votre situation familiale : ${props.situationFrench}</li>` +
        `<li>de votre nombre d'enfant à charge : ${props.childrenCount}</li>` +
        `<li>de l'âge de l'enfant accueilli : ${props.childAgeFrench}</li></ul>`;
      if (props.handicapToggle) body += props.handicapToggleInfos;
      const name = props.brandName || 'notre outil';
      body += '<div class="help-notice small notice"><div class="bubble">' +
        `<p>Si vous utilisez <a href="${toolsLink.value}" class="dotted" target="_blank">nos outils de gestion</a>, ${name} vérifie chaque mois votre droit aux aides <b>et vous aide à les optimiser !</b></p>` +
        '</div></div>';
      return body;
    });

    return {
      isGed,
      cafCmgSource,
      pajemploiCmgSource,
      blurClass,
      toolsLink,
      paidToEmployeeLabel,
      paidToEmployeeDetailLabel,
      paidToEmployeeHelp,
      employeeContributionsHelp,
      employerContributionsHelp,
      totalMonthlyHelp,
      employerReductionHelp,
      cafCoverageLabel,
      mensHelp,
      overtimeHelp,
      vacationHelp,
      maintenanceHelp,
      mealHelp,
      cafCoverageHelp,
      cmgAidHelp,
      iconQuestion,
    };
  },
});
</script>
